import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Paper, TextField, Button, Spinner, Alert, Chip, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { DANGER_BTN, NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  AdminApiError,
  type UpcReviewItem,
  type UpcReviewDecision,
} from '../lib/adminApiClient';

/**
 * UPC correction review queue (2026-07-12 check-digit migration leftovers +
 * ongoing probe output).
 *
 * Full barcodes are NEVER shown (feedback_never_show_upc): reviewers see
 * masked last-4 tails and decide on the EXTERNAL EVIDENCE — what Barcode
 * Lookup / Edamam resolved each interpretation to. A "Kroger-form" decision
 * doesn't touch the catalog directly; the upcCheckDigitMigration bg-job
 * applies confirmed rows on its nightly run or via "Apply confirmed now".
 */

const PENDING_FILTERS = [
  { key: '', label: 'All pending' },
  { key: 'both-resolve', label: 'Both resolve' },
  { key: 'unresolved', label: 'Unresolved' },
  { key: 'suspect-no-provenance', label: 'Suspects' },
] as const;

export default function UpcReviewQueue() {
  const caps = useCapabilities();
  const canResolve = caps.has('upcreview:resolve');
  const canRunJobs = caps.has('job:run');

  const [items, setItems] = useState<UpcReviewItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listUpcReview('pending', filter || undefined);
      setItems(res.items);
      setCounts(res.classificationCounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const onActionDone = (kind: 'success' | 'error', msg: string) => {
    setFlash({ kind, msg });
    void load();
  };

  const confirmedPending = counts['confirmed-kroger-form'] ?? 0;

  const applyNow = async () => {
    setApplying(true);
    try {
      // Renames now; the job's merge pass stays a deliberate separate run
      // (JobRunner) so twin folds get a dryRun review first.
      await adminApiClient.runJob('upcCheckDigitMigration', {
        params: { dryRun: false, phase: 'renames', limit: 6000 },
        confirm: 'upcCheckDigitMigration',
      });
      onActionDone('success', 'Apply started — confirmed corrections are being rekeyed. Track it in the Job runner.');
    } catch (err) {
      onActionDone('error', err instanceof AdminApiError ? err.message : 'Failed to start the apply job');
    } finally {
      setApplying(false);
    }
  };

  if (caps.loading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            UPC review queue
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {items.length} item{items.length === 1 ? '' : 's'} awaiting a decision.
            {confirmedPending > 0 && ` ${confirmedPending} confirmed correction${confirmedPending === 1 ? '' : 's'} waiting to apply.`}
          </Typography>
        </Box>
        {canRunJobs && (
          <Button
            variant="contained"
            ariaLabel="Apply confirmed corrections now"
            buttonText={applying ? 'Starting…' : 'Apply confirmed now'}
            disabled={applying || confirmedPending === 0}
            onClick={() => void applyNow()}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {PENDING_FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={`${f.label}${f.key && counts[f.key] != null ? ` (${counts[f.key]})` : ''}`}
            color={filter === f.key ? 'primary' : 'default'}
            onClick={() => setFilter(f.key)}
            size="small"
          />
        ))}
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind === 'success' ? 'success' : 'error'}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {items.length === 0 ? (
        <EmptyState icon="check_circle" title="All caught up" body="No UPC decisions are waiting." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((item) => (
            <UpcReviewRow key={item.glpProductId} item={item} canResolve={canResolve} onDone={onActionDone} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function EvidenceLine({ label, tail, hit }: { label: string; tail: string | null; hit: { source: string; title: string | null } | null }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 700, width: 88, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>····{tail ?? '????'}</Typography>
      {hit ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {hit.source === 'barcode_lookup' ? 'Barcode Lookup' : 'Edamam'}: {hit.title ?? '(match, no title captured)'}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>no external match</Typography>
      )}
    </Box>
  );
}

function UpcReviewRow({
  item,
  canResolve,
  onDone,
}: {
  item: UpcReviewItem;
  canResolve: boolean;
  onDone: (kind: 'success' | 'error', msg: string) => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const product = item.product;

  const classify = async (decision: UpcReviewDecision, okMsg: string) => {
    setBusy(true);
    try {
      await adminApiClient.classifyUpcReview(item.glpProductId, {
        decision,
        note: note.trim() || undefined,
      });
      onDone('success', okMsg);
    } catch (err) {
      onDone('error', err instanceof AdminApiError ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box
          component="img"
          src={product?.primaryThumbUrl ?? product?.primaryImageUrl ?? ''}
          alt=""
          sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, bgcolor: '#eee', flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {product?.itemName ?? '(product missing)'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {[product?.brand, product?.glpDepartment].filter(Boolean).join(' · ') || '—'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip label={item.classification} size="small" color="warning" />
            <Chip label={item.origin === 'tier1-no-provenance' ? 'no Kroger provenance' : 'probe'} size="small" />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}>
        <EvidenceLine label="Stored" tail={item.storedLast4} hit={item.storedHit} />
        <EvidenceLine label="Corrected" tail={item.correctedLast4} hit={item.correctedHit} />
      </Box>

      {canResolve && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            ariaLabel="Stored code is Kroger-form; correct it"
            buttonText="Kroger-form — correct it"
            disabled={busy}
            onClick={() => void classify('confirmed-kroger-form', 'Marked Kroger-form — applies on the next migration run.')}
          />
          <Button
            variant="outlined"
            sx={NEUTRAL_BTN}
            color="primary"
            ariaLabel="Stored code is a true UPC; keep it"
            buttonText="True code — keep"
            disabled={busy}
            onClick={() => void classify('confirmed-true', 'Kept as a true UPC.')}
          />
          <Button
            variant="outlined"
            sx={NEUTRAL_BTN}
            color="primary"
            ariaLabel="Not a UPC; keep the stored code verbatim"
            buttonText="Keep verbatim (non-UPC)"
            disabled={busy}
            onClick={() => void classify('keep-verbatim', 'Kept verbatim — excluded from future reports.')}
          />
          <Button
            variant="outlined"
            sx={DANGER_BTN}
            color="error"
            ariaLabel="Mark unrepairable"
            buttonText="Unrepairable"
            disabled={busy}
            onClick={() => void classify('unrepairable', 'Parked as unrepairable.')}
          />
          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Box>
      )}
    </Paper>
  );
}
